export type LocaleMap = {
    [key: string]: string | LocaleMap;
};

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

type DotJoin<P extends string, K extends string> = P extends '' ? K : `${P}.${K}`;

type FlattenEntry<T, P extends string = ''> = T extends string
    ? object
    : {
          [K in Extract<keyof T, string>]: T[K] extends string
              ? { [Key in DotJoin<P, K>]: T[K] }
              : T[K] extends Record<string, any>
                ? FlattenEntry<T[K], DotJoin<P, K>>
                : object;
      }[Extract<keyof T, string>];

export type Flatten<T> = UnionToIntersection<FlattenEntry<T>>;

export const flatten = <T extends LocaleMap>(localeMap: T, prefix = '') =>
    Object.entries(localeMap).reduce(
        (acc, [key, val]) => {
            if (typeof val === 'string') {
                const newKey = prefix.length ? `${prefix}.${key}` : key;
                acc[newKey] = val;

                return acc;
            }

            const newPrefix = prefix.length ? `${prefix}.${key}` : `${key}`;
            const innerMap = flatten(val, newPrefix);

            Object.entries(innerMap).forEach(([innerKey, innerVal]) => {
                acc[innerKey] = innerVal;
            });

            return acc;
        },
        {} as Record<string, string>,
    ) as Flatten<T>;
